import { apiFetchJson, apiFetchVoid } from '../http/apiFetch';
import type {
  DiscountCode,
  DiscountCodeCompanyRef,
  DiscountCodeCreatePayload,
  DiscountCodeFilters,
  DiscountCodeSortKey,
  DiscountCodeStatus,
  DiscountCodeType,
  DiscountCodeUpdatePayload,
  SortDirection,
} from '../../models/discountCode';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeType(value: unknown): DiscountCodeType {
  return asString(value).trim().toLowerCase() === 'fixed' ? 'fixed' : 'percentage';
}

function normalizeStatus(value: unknown): DiscountCodeStatus {
  const normalized = asString(value).trim().toLowerCase();
  if (normalized === 'inactive') {
    return 'inactive';
  }
  if (normalized === 'expired') {
    return 'expired';
  }
  return 'active';
}

function normalizeCompanyRef(row: unknown): DiscountCodeCompanyRef | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const c = row as Record<string, unknown>;
  const companyId = asString(c.companyId ?? c.CompanyId).trim();
  if (!companyId) {
    return null;
  }
  return {
    companyId,
    companyName: asString(c.companyName ?? c.CompanyName).trim(),
  };
}

export function normalizeDiscountCode(row: unknown): DiscountCode | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const d = row as Record<string, unknown>;
  const id = asString(d.id ?? d.Id).trim();
  if (!id) {
    return null;
  }
  const rawCompanies = d.assignedCompanies ?? d.AssignedCompanies;
  const assignedCompanies = Array.isArray(rawCompanies)
    ? rawCompanies.map(normalizeCompanyRef).filter((c): c is DiscountCodeCompanyRef => Boolean(c))
    : [];
  return {
    id,
    code: asString(d.code ?? d.Code).trim(),
    type: normalizeType(d.type ?? d.Type),
    amount: asNumber(d.amount ?? d.Amount),
    status: normalizeStatus(d.status ?? d.Status),
    expiresAt: asOptionalString(d.expiresAt ?? d.ExpiresAt),
    stripeCouponId: asOptionalString(d.stripeCouponId ?? d.StripeCouponId),
    createdBy: asString(d.createdBy ?? d.CreatedBy).trim(),
    createdAt: asString(d.createdAt ?? d.CreatedAt),
    assignedCompanies,
  };
}

/** Emits a lightweight client-side audit signal (backend is the authoritative trail). */
export function logDiscountCodeAudit(
  event: 'access' | 'filter' | 'create' | 'update' | 'delete',
  details: Record<string, unknown>
): void {
  const payload = { event, timestamp: new Date().toISOString(), ...details };
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('bo-admin-discount-code-audit', { detail: payload }));
  }
  console.info('[AdminDiscountCodeAudit]', payload);
}

export async function fetchDiscountCodes(
  getAccessToken: () => string | undefined,
  filters?: DiscountCodeFilters,
  companyId?: string
): Promise<DiscountCode[]> {
  const params = new URLSearchParams();
  if (filters?.status && filters.status.toLowerCase() !== 'all') {
    params.set('status', filters.status);
  }
  if (filters?.search?.trim()) {
    params.set('search', filters.search.trim());
  }
  if (companyId?.trim()) {
    params.set('companyId', companyId.trim());
  }
  const query = params.toString();
  const path = `/api/admin/discount-codes${query ? `?${query}` : ''}`;

  const result = await apiFetchJson<unknown>(path, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  const rows = Array.isArray(result)
    ? result
    : result && typeof result === 'object' && Array.isArray((result as { value?: unknown }).value)
      ? (result as { value: unknown[] }).value
      : [];
  return rows.map(normalizeDiscountCode).filter((d): d is DiscountCode => Boolean(d));
}

export async function createDiscountCode(
  payload: DiscountCodeCreatePayload,
  getAccessToken: () => string | undefined
): Promise<DiscountCode | null> {
  const result = await apiFetchJson<unknown>('/api/admin/discount-codes', {
    method: 'POST',
    getAccessToken: async () => getAccessToken(),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return normalizeDiscountCode(result);
}

export async function updateDiscountCode(
  id: string,
  payload: DiscountCodeUpdatePayload,
  getAccessToken: () => string | undefined
): Promise<DiscountCode | null> {
  const result = await apiFetchJson<unknown>(`/api/admin/discount-codes/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    getAccessToken: async () => getAccessToken(),
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  return normalizeDiscountCode(result);
}

export async function deleteDiscountCode(
  id: string,
  getAccessToken: () => string | undefined
): Promise<void> {
  await apiFetchVoid(`/api/admin/discount-codes/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    getAccessToken: async () => getAccessToken(),
  });
}

/** Client-side filter over the already-fetched list (mirrors the backend status/search filter). */
export function filterDiscountCodes(codes: DiscountCode[], filters: DiscountCodeFilters): DiscountCode[] {
  const normalizedStatus = (filters.status ?? '').trim().toLowerCase();
  const normalizedSearch = (filters.search ?? '').trim().toLowerCase();

  return codes.filter((code) => {
    const matchesStatus =
      !normalizedStatus || normalizedStatus === 'all' || code.status === normalizedStatus;
    const matchesSearch =
      !normalizedSearch ||
      code.code.toLowerCase().includes(normalizedSearch) ||
      code.assignedCompanies.some((c) => c.companyName.toLowerCase().includes(normalizedSearch));
    return matchesStatus && matchesSearch;
  });
}

function compareCodes(a: DiscountCode, b: DiscountCode, key: DiscountCodeSortKey): number {
  switch (key) {
    case 'amount':
      return a.amount - b.amount;
    case 'expiresAt': {
      const left = a.expiresAt ? new Date(a.expiresAt).getTime() : Number.POSITIVE_INFINITY;
      const right = b.expiresAt ? new Date(b.expiresAt).getTime() : Number.POSITIVE_INFINITY;
      return left - right;
    }
    case 'type':
      return a.type.localeCompare(b.type);
    case 'status':
      return a.status.localeCompare(b.status);
    case 'code':
    default:
      return a.code.localeCompare(b.code, undefined, { sensitivity: 'base' });
  }
}

/** Returns a new array sorted by the given key/direction. Does not mutate the input. */
export function sortDiscountCodes(
  codes: DiscountCode[],
  sortKey: DiscountCodeSortKey,
  sortDirection: SortDirection
): DiscountCode[] {
  return [...codes].sort((a, b) => {
    const result = compareCodes(a, b, sortKey);
    return sortDirection === 'asc' ? result : -result;
  });
}

export function isDiscountCodeExpired(code: DiscountCode): boolean {
  return code.status === 'expired';
}
